import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import { FormattedMessage } from "react-intl";
import ApiService from "../services/ApiService";
import { useAuthorization } from "../../authorizer/components/AuthorizationProvider";
import InfoIcon from "@material-ui/icons/Info";

interface AudienceClaimEnforcementToggleProps {
  deploymentId?: string;
  serviceId?: string;
  enabled?: boolean;
  onUpdated?: () => void;
}

const AudienceClaimEnforcementToggle: React.FC<
  AudienceClaimEnforcementToggleProps
> = ({ deploymentId, serviceId, enabled, onUpdated }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { isActionAllowed } = useAuthorization("SERVICE", serviceId || "");
  const canUpdate = isActionAllowed("update");

  const isEnabled = !!enabled;

  const handleToggleClick = () => {
    if (isEnabled) {
      // Cannot be disabled once enabled; the Switch is rendered disabled below.
      return;
    }
    setError(undefined);
    setConfirmChecked(false);
    setModalOpen(true);
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setModalOpen(false);
  };

  const handleEnable = () => {
    if (!deploymentId) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    ApiService.updateDeploymentConfig(
      { audience_enforcement: true },
      deploymentId
    ).subscribe({
      next: () => {
        setSubmitting(false);
        setModalOpen(false);
        onUpdated?.();
      },
      error: (err: any) => {
        setSubmitting(false);
        setError(
          err?.response?.data?.message ??
            "Unable to enable audience claim validation. Please try again."
        );
      },
    });
  };

  return (
    <>
      <FormControl>
        <Switch
          size="small"
          color="primary"
          checked={isEnabled}
          disabled={isEnabled || !canUpdate}
          onChange={handleToggleClick}
        />
      </FormControl>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        disableBackdropClick={submitting}
        disableEscapeKeyDown={submitting}
      >
        <DialogTitle disableTypography style={{ paddingBottom: 8 }}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Typography variant="h6" style={{ fontWeight: 600, fontSize: 18 }}>
              <FormattedMessage defaultMessage="Enable Audience Claim Validation?" />
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              disabled={submitting}
              style={{ marginTop: -4, marginRight: -8 }}
              aria-label="close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0 }}>
          <Typography component="p" variant="body2" style={{ fontSize: 14, marginBottom: 16 }}>
            <FormattedMessage defaultMessage="Enabling Audience Claim Validation configures the API Gateway to validate the aud (Audience) claim in JWT access tokens for this deployment." />
          </Typography>
          <Typography component="p" variant="body2" style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
            <FormattedMessage defaultMessage="After this feature is enabled:" />
          </Typography>
          <Typography component="p" variant="caption" style={{ display: "block", fontSize: 12, marginBottom: 16 }}>
            <FormattedMessage defaultMessage="Only JWT tokens containing the configured audience value will be accepted. Requests with missing or invalid audience claims will be rejected with an authorization error. This improves API security by ensuring tokens are intended specifically for this API deployment." />
          </Typography>
          <Alert variant="outlined" severity="info"
                 style={{ margin: "16px 0px" }}>
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: 14, color: "#0063a3" }}>
              <FormattedMessage defaultMessage="This is one time irreversible security change" />
            </Typography>
            <Typography variant="caption" style={{ display: "block", fontSize: 12, color: "#0063a3" }}>
              <FormattedMessage defaultMessage="Once enabled, audience claim validation cannot be turned off for this deployment." />
            </Typography>
          </Alert>

          <Typography component="p" variant="caption" style={{ display: "block", fontSize: 12, marginBottom: 8 }}>
            <FormattedMessage defaultMessage="Before continuing, ensure that all client applications are issuing JWT tokens with the correct audience claim." />
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={
              <Typography variant="caption" style={{ fontSize: 12 }}>
                <FormattedMessage defaultMessage="I understand that this action is permanent and cannot be reverted." />
              </Typography>
            }
          />

          {error && (
            <Alert severity="error" style={{ marginTop: 12 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "16px 24px" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClose}
            disabled={submitting}
          >
            <FormattedMessage defaultMessage="Cancel" />
          </Button>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!confirmChecked || submitting}
            onClick={handleEnable}
          >
            <FormattedMessage defaultMessage="Enable Validation" />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { AudienceClaimEnforcementToggle };
